/**
 * AdminRenewalRulesPage — Phase 9F
 *
 * Quick-reference table of all 51 US jurisdictions' renewal rules with
 * inline editing on mechanism, pro-se note, and no-pathway display mode.
 * Row click opens a right-side drawer with the full version list (add /
 * edit / delete versions).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  Check,
  Info,
  Loader2,
  Pencil,
  Plus,
  Search,
  ScrollText,
  X,
} from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import RenewalRuleDrawer from "@/pages/admin/renewal-rules/RenewalRuleDrawer";

// ---------------------------------------------------------------------------
// Inline editable text cell (single-line)
// ---------------------------------------------------------------------------
const InlineTextCell = ({ value, onSave, testid, placeholder = "—", disabled = false, maxLen = 240 }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(value || ""), [value]);

  if (disabled) {
    return <span className="text-text-muted text-[13px]">{value || placeholder}</span>;
  }

  const commit = async () => {
    if ((draft || "") === (value || "")) {
      setEditing(false);
      return;
    }
    if (draft.length > maxLen) {
      toast.error(`Value too long (max ${maxLen} chars)`);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="text-left text-white text-[13px] hover:bg-accent-pink/10 hover:text-accent-pink px-2 py-1 rounded-[4px] transition-colors w-full truncate"
        data-testid={testid}
        title={value || placeholder}
      >
        {value || <span className="text-text-muted italic">{placeholder}</span>}
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 w-full" data-testid={`${testid}-editor`} onClick={(e) => e.stopPropagation()}>
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") cancel();
        }}
        className="flex-1 h-8 px-2 rounded-[6px] bg-bg-tertiary border border-accent-pink text-white text-[13px] focus:outline-none focus:shadow-pink-glow"
      />
      <button
        type="button"
        onClick={commit}
        disabled={saving}
        className="w-7 h-7 rounded-[6px] bg-accent-pink text-[#091424] hover:bg-accent-pink-light inline-flex items-center justify-center disabled:opacity-50"
        data-testid={`${testid}-save`}
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
      </button>
      <button
        type="button"
        onClick={cancel}
        className="w-7 h-7 rounded-[6px] bg-bg-tertiary border border-border-default text-text-secondary hover:text-white inline-flex items-center justify-center"
        data-testid={`${testid}-cancel`}
      >
        <X size={12} />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Display-mode dropdown (only enabled when current pathway is NO)
// ---------------------------------------------------------------------------
const DisplayModeCell = ({ value, disabled, onSave, testid }) => {
  const [saving, setSaving] = useState(false);
  if (disabled) {
    return (
      <span className="text-text-muted text-[12px] italic" data-testid={`${testid}-na`}>
        N/A (pathway is YES)
      </span>
    );
  }
  return (
    <select
      value={value}
      disabled={saving}
      onChange={async (e) => {
        setSaving(true);
        try {
          await onSave(e.target.value);
        } catch (err) {
          toast.error(formatApiError(err));
        } finally {
          setSaving(false);
        }
      }}
      onClick={(e) => e.stopPropagation()}
      className="h-8 px-2 rounded-[6px] bg-bg-tertiary border border-border-default text-white text-[12px] focus:outline-none focus:border-accent-pink"
      data-testid={testid}
    >
      <option value="attorney_required">Attorney Required</option>
      <option value="no_pathway">No Renewal Pathway</option>
    </select>
  );
};

const PathwayBadge = ({ pathway }) => (
  <span
    className={cn(
      "inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold tracking-[0.06em] uppercase border",
      pathway === "YES"
        ? "bg-success/15 text-success border-success/40"
        : "bg-text-muted/15 text-text-secondary border-border-default"
    )}
    data-testid={`pathway-badge-${pathway}`}
  >
    {pathway}
  </span>
);

// ---------------------------------------------------------------------------
// Table header cell with sort toggle
// ---------------------------------------------------------------------------
const HeaderCell = ({ label, sortKey, currentSort, onSort, testid }) => {
  const active = currentSort?.key === sortKey;
  return (
    <th
      className="jea-caption text-text-secondary p-3 text-left cursor-pointer select-none hover:text-white transition-colors"
      onClick={() => onSort(sortKey)}
      data-testid={testid}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-accent-pink">{currentSort.dir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AdminRenewalRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "state_code", dir: "asc" });
  const [drawerStateCode, setDrawerStateCode] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/renewal-rules");
      setRules(data.rules || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? rules.filter((r) =>
          r.state_code.toLowerCase().includes(q) ||
          r.state_name.toLowerCase().includes(q) ||
          (r.current_mechanism || "").toLowerCase().includes(q)
        )
      : rules.slice();
    const dir = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const av = a[sort.key] ?? "";
      const bv = b[sort.key] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return list;
  }, [rules, search, sort]);

  const toggleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  const patchLatestVersion = async (state_code, patch) => {
    // Grab the state's LATEST-in-effect version and patch it.
    const { data } = await apiClient.get(`/admin/renewal-rules/${state_code}`);
    const versions = data.rule.versions || [];
    // Server-sorted with null-last; latest applicable version = first with
    // effective_from <= today, else fall back to the baseline (null).
    const today = new Date().toISOString().slice(0, 10);
    const candidates = versions
      .filter((v) => !v.effective_from || v.effective_from <= today)
      .sort((a, b) => {
        const ae = a.effective_from || "";
        const be = b.effective_from || "";
        return ae.localeCompare(be);
      });
    const target = candidates[candidates.length - 1] || versions[0];
    if (!target) throw new Error("No version to update");
    await apiClient.patch(
      `/admin/renewal-rules/${state_code}/versions/${target.version_id}`,
      patch
    );
    await load();
  };

  const patchStateField = async (state_code, patch) => {
    await apiClient.patch(`/admin/renewal-rules/${state_code}`, patch);
    await load();
  };

  const runSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await apiClient.post("/admin/renewal-rules/seed");
      toast.success(`Seed complete: ${data.inserted} inserted, ${data.existing} existing`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-renewal-rules-page">
      <JeaCard className="bg-warning/5 border-warning/30 flex items-start gap-3">
        <Info size={16} className="text-warning shrink-0 mt-0.5" />
        <div className="text-[13px] text-text-secondary leading-6">
          <span className="text-white font-semibold">Rule changes take effect immediately for all NEW judgments </span>
          entered on or after the version's effective date. Existing judgments entered before the effective date remain
          governed by the older rule (grandfathering). AZ pre-2013 unrenewed judgments remain permanently expired.
        </div>
      </JeaCard>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[280px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            type="text"
            placeholder="Search state name, code, or mechanism…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-9 pr-3 rounded-[8px] bg-bg-secondary border border-border-default text-white text-[13px] focus:outline-none focus:border-accent-pink focus:shadow-pink-glow"
            data-testid="admin-renewal-rules-search"
          />
        </div>
        <span className="text-text-secondary text-[12px] tracking-[0.06em] uppercase font-semibold">
          Showing {filtered.length} of {rules.length}
        </span>
      </div>

      {loading ? (
        <div className="space-y-2" data-testid="admin-renewal-rules-loading">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
        </div>
      ) : rules.length === 0 ? (
        <JeaCard className="text-center py-12" data-testid="admin-renewal-rules-empty">
          <ScrollText size={40} className="mx-auto text-text-muted mb-3" />
          <div className="text-white font-bold text-[16px] mb-2">No renewal rules loaded</div>
          <p className="text-text-secondary text-[13px] mb-4">
            The state_renewal_rules collection is empty. Click below to seed all 51 jurisdictions.
          </p>
          <button
            type="button"
            onClick={runSeed}
            disabled={seeding}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-[8px] bg-accent-pink text-[#091424] hover:bg-accent-pink-light font-bold text-[13px] disabled:opacity-50"
            data-testid="admin-renewal-rules-seed-cta"
          >
            {seeding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Seed Default Rules
          </button>
        </JeaCard>
      ) : (
        <JeaCard className="p-0 overflow-hidden" data-testid="admin-renewal-rules-table-card">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px]">
              <thead className="sticky top-0 bg-bg-secondary z-10">
                <tr className="border-b border-border-default">
                  <HeaderCell label="State"                  sortKey="state_code"                   currentSort={sort} onSort={toggleSort} testid="col-state" />
                  <HeaderCell label="Renewal YES/NO"         sortKey="current_pathway"              currentSort={sort} onSort={toggleSort} testid="col-pathway" />
                  <HeaderCell label="Mechanism"              sortKey="current_mechanism"            currentSort={sort} onSort={toggleSort} testid="col-mechanism" />
                  <th className="jea-caption text-text-secondary p-3 text-left">Pro-Se Feasibility Note</th>
                  <HeaderCell label="NO-Pathway Display Mode" sortKey="no_pathway_display_mode"      currentSort={sort} onSort={toggleSort} testid="col-display-mode" />
                  <HeaderCell label="Versions"               sortKey="version_count"                currentSort={sort} onSort={toggleSort} testid="col-versions" />
                  <th className="jea-caption text-text-secondary p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isNo = r.current_pathway === "NO";
                  return (
                    <motion.tr
                      key={r.state_code}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}
                      className="border-b border-border-default hover:bg-bg-tertiary transition-colors cursor-pointer"
                      onClick={() => setDrawerStateCode(r.state_code)}
                      data-testid={`admin-renewal-row-${r.state_code}`}
                    >
                      <td className="p-3">
                        <div className="text-white font-semibold text-[13px]">{r.state_name}</div>
                        <div className="jea-mono text-[11px] text-text-secondary">{r.state_code}</div>
                      </td>
                      <td className="p-3"><PathwayBadge pathway={r.current_pathway} /></td>
                      <td className="p-3 max-w-[220px]">
                        <InlineTextCell
                          value={r.current_mechanism}
                          testid={`admin-renewal-mechanism-${r.state_code}`}
                          onSave={(val) => patchLatestVersion(r.state_code, { mechanism: val, renewal_pathway: r.current_pathway })}
                          maxLen={120}
                        />
                      </td>
                      <td className="p-3 max-w-[320px]">
                        <InlineTextCell
                          value={r.current_pro_se_note}
                          testid={`admin-renewal-note-${r.state_code}`}
                          onSave={(val) => patchLatestVersion(r.state_code, { pro_se_feasibility_note: val, renewal_pathway: r.current_pathway })}
                          maxLen={240}
                        />
                      </td>
                      <td className="p-3">
                        <DisplayModeCell
                          value={r.no_pathway_display_mode}
                          disabled={!isNo}
                          testid={`admin-renewal-display-mode-${r.state_code}`}
                          onSave={(val) => patchStateField(r.state_code, { no_pathway_display_mode: val })}
                        />
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDrawerStateCode(r.state_code); }}
                          className="inline-flex items-center gap-1.5 h-8 px-2 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/60 text-[12px] font-semibold transition-colors"
                          data-testid={`admin-renewal-versions-${r.state_code}`}
                        >
                          {r.version_count} version{r.version_count !== 1 && "s"}
                        </button>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setDrawerStateCode(r.state_code)}
                            className="inline-flex items-center gap-1.5 h-8 px-2 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/60 text-[12px] font-semibold transition-colors"
                            data-testid={`admin-renewal-edit-${r.state_code}`}
                          >
                            <Pencil size={11} /> Edit
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </JeaCard>
      )}

      <AnimatePresence>
        {drawerStateCode && (
          <RenewalRuleDrawer
            stateCode={drawerStateCode}
            onClose={() => setDrawerStateCode(null)}
            onChanged={load}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
